import { Box, WizardStepRenderParams, useOnNext } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import { Input } from '@factorialco/f0-react/dist/experimental'
import {
  CurrencySelectorCurrencyFragment,
  ValidateBankingBankAccountNumberDocument,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { WizardData } from 'modules/banking/components/NewAccountModal/types'
import { WizardStepLayout } from 'modules/banking/components/NewAccountModal/wizardSteps/WizardStepLayout'
import {
  accountNumberExtraFieldLabel,
  accountNumberLabel,
  bankAccountFormatOptions,
  toBankAccountFormat,
} from 'modules/banking/lib/bankAccountNumbersHelpers'

interface Props {
  currencies: CurrencySelectorCurrencyFragment[]
  errors: {
    accountNumber?: string
    complementaryData?: string
  }
  onErrorsChange: (errors: { accountNumber?: string; complementaryData?: string }) => void
  existingAccountNumbers?: string[]
}

export const BankAccountManualInformationWizardStepF0 = ({
  data: wizardData,
  onDataChanged,
  currencies,
  errors,
  onErrorsChange,
  existingAccountNumbers = [],
}: WizardStepRenderParams<WizardData> & Props) => {
  const [validateBankingInformation] = useMutation(ValidateBankingBankAccountNumberDocument, {
    extractorFn: (data) => data.bankingMutations.validateBankingBankAccountNumber,
  })
  const bankAccountManualInfoForm = useForm({
    defaultValues: {
      accountNumber: wizardData.accountNumber,
      complementaryData: wizardData.complementaryData,
      accountNumberType: wizardData.accountNumberType,
    },
    onSubmit: async () => {
      const variables = {
        accountNumber: wizardData.accountNumber || '',
        complementaryData: wizardData.complementaryData,
        format: wizardData.accountNumberType,
      }

      const result = await validateBankingInformation({ variables })

      const handled = handleResult(result, (err) => {
        bankAccountManualInfoForm.setErrors({
          accountNumber: err['accountNumber'],
          complementaryData: err['complementaryData'],
        })
      })

      if (!handled.success) {
        onErrorsChange({
          accountNumber: i18n.t('employee_form.bank_info.invalid_format'),
          complementaryData: bankAccountManualInfoForm.values.complementaryData
            ? i18n.t('employee_form.bank_info.invalid_format')
            : undefined,
        })
      }
      return handled
    },
  })

  const submitForm = async () => {
    const submitResult = await bankAccountManualInfoForm.submitForm()

    if (!submitResult.success) {
      throw new Error(submitResult.errors.join())
    }
  }

  const accountLabel = accountNumberLabel(wizardData.accountNumberType)
  const extraFieldLabel = accountNumberExtraFieldLabel(wizardData.accountNumberType)

  useOnNext(submitForm)

  const handleFieldChanged = useUnsafeCallback(
    (fieldName: string, fieldValue: string | undefined | number) => {
      onDataChanged({
        ...wizardData,
        [fieldName]: fieldValue,
      })
    },
    [wizardData, onDataChanged]
  )

  const onAccountNumberChange = useUnsafeCallback(
    (value: string) => {
      handleFieldChanged('accountNumber', value)
      const normalized = value.toUpperCase().replace(/\s/g, '')
      if (
        normalized &&
        existingAccountNumbers.some(
          (existing) => existing.toUpperCase().replace(/\s/g, '') === normalized
        )
      ) {
        onErrorsChange({
          accountNumber: i18n.t(
            'banking.accounts.new_wizard.step_3_0.duplicate_account_number'
          ),
        })
      } else {
        onErrorsChange({})
      }
    },
    [handleFieldChanged, onErrorsChange, existingAccountNumbers]
  )

  const onExtraFieldChange = useUnsafeCallback(
    (value: string) => {
      handleFieldChanged('complementaryData', value)
      onErrorsChange({ accountNumber: errors.accountNumber })
    },
    [handleFieldChanged, onErrorsChange, errors.accountNumber]
  )

  const onCurrencyChange = useUnsafeCallback(
    (value: string) => {
      onDataChanged({ ...wizardData, currency: value })
    },
    [wizardData, onDataChanged]
  )

  const onAccountAliasChange = useUnsafeCallback(
    (value: string) => {
      onDataChanged({ ...wizardData, accountAlias: value })
    },
    [wizardData, onDataChanged]
  )

  const onAccountNumberTypeChange = useUnsafeCallback(
    (newFormat: string) => {
      const format = toBankAccountFormat(newFormat)
      if (format === 'other' || format === 'clabe') {
        onDataChanged({
          ...wizardData,
          complementaryData: '',
          accountNumberType: format,
        })
      } else {
        onDataChanged({
          ...wizardData,
          accountNumberType: format,
        })
      }
      onErrorsChange({})
    },
    [onDataChanged, onErrorsChange, wizardData]
  )

  // Convert bankAccountFormatOptions to F0Select format
  const formatOptions = bankAccountFormatOptions().map((opt) => ({
    value: opt.value,
    label: opt.label,
  }))

  // Convert currencies to F0Select format
  const currencyOptions = currencies.map((currency) => ({
    value: currency.isoCode,
    label: currency.isoCode,
  }))

  return (
    <WizardStepLayout
      title={i18n.t('banking.accounts.new_wizard.step_3_0.title')}
      subtitle={i18n.t('banking.accounts.new_wizard.step_3_0.subtitle')}
    >
      <Box style={{ width: '500px' }} flexNoShrink flexDirection="column" gap="s16">
        <Input
          label={i18n.t('banking.accounts.side_panel.account_alias')}
          value={wizardData.accountAlias}
          placeholder={i18n.t('banking.accounts.side_panel.account_alias')}
          onChange={onAccountAliasChange}
        />

        <F0Select
          label={i18n.t('employee_form.bank_info.format')}
          value={wizardData.accountNumberType}
          options={formatOptions}
          onChange={onAccountNumberTypeChange}
        />

        <Input
          label={accountLabel}
          value={wizardData.accountNumber}
          placeholder={i18n.t('banking.accounts.side_panel.account_number')}
          onChange={onAccountNumberChange}
          status={
            errors.accountNumber ? { type: 'error', message: errors.accountNumber } : undefined
          }
          required
        />

        {extraFieldLabel && (
          <Input
            label={extraFieldLabel}
            value={wizardData.complementaryData}
            onChange={onExtraFieldChange}
            status={
              errors.complementaryData
                ? { type: 'error', message: errors.complementaryData }
                : undefined
            }
          />
        )}

        <F0Select
          label={i18n.t('banking.accounts.side_panel.currency')}
          value={wizardData.currency}
          options={currencyOptions}
          onChange={onCurrencyChange}
        />
      </Box>
    </WizardStepLayout>
  )
}
